import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * Per-loan record persisted to `InvestorReporting.reportData` for each loan that
 * the GNMA RFS Monthly Report (Appendix VI-19) emits. One row per emitted loan;
 * pool-level (P-record) state lives on `PoolRfsReportedState`, and loan-level
 * change-detection state lives on `LoanRfsReportedState` — this record is the
 * audit/display artifact of what was actually reported for the loan.
 *
 * Fields mirror the fixed-width L-record builder inputs (active + liquidating).
 * Per the JSON-persistence convention, leaf fields are optional so the schema
 * can evolve without breaking parsing of existing blobs; only the version,
 * mode, and loan identity are required.
 *
 * PII rule (Decision #23): the S-record's borrower identity data is NEVER
 * persisted here. We record only `sensitiveRecordEmitted` — the encrypted hash
 * that drives change detection lives on `LoanRfsReportedState`.
 */
export const GinnieMaeRfsReportedLoanMode = z.enum(['active', 'liquidating']);
export type GinnieMaeRfsReportedLoanMode = z.infer<typeof GinnieMaeRfsReportedLoanMode>;

export const GinnieMaeRfsReportedRecordV1 = z.object({
  version: z.literal('1'),
  mode: GinnieMaeRfsReportedLoanMode,
  poolId: z.string(),
  ginnieMaeUniqueLoanId: z.string(),

  loanType: z.enum(['FHA', 'FH1', 'FMF', 'RHS', 'RMF', 'PIH', 'VAG', 'VAV']).optional(),
  caseNumber: z.string().optional(),
  issuerLoanId: z.string().optional(),

  firstPaymentDate: zodDateOrString.optional(),
  loanMaturityDate: zodDateOrString.optional(),
  lastInstallmentPaidDate: zodDateOrString.optional(),
  actualPaymentDate: zodDateOrString.optional(),
  removalDate: zodDateOrString.optional(),

  loanInterestRate: z.number().optional(),
  loanOpb: z.number().optional(),
  loanFic: z.number().optional(),
  inForeclosure: z.boolean().optional(),
  delinquentInterest: z.number().optional(),
  delinquentPrincipal: z.number().optional(),
  prepaidInterest: z.number().optional(),
  prepaidPrincipal: z.number().optional(),
  installInterest: z.number().optional(),
  installPrincipal: z.number().optional(),
  curtailment: z.number().optional(),
  adjustInterest: z.number().optional(),
  netAdjustUpb: z.number().optional(),
  actualLoanUpb: z.number().optional(),
  loanTaxAndInsuranceBalance: z.number().optional(),
  scheduledUpb: z.number().optional(),
  scheduledMonthlyPrincipalAmount: z.number().optional(),
  scheduledMonthlyInterestAmount: z.number().optional(),
  grossServiceFeeCollected: z.number().optional(),
  curtailmentPrincipalCode: z.enum(['1', '2', '3', '4']).optional(),

  // Liquidation-only (mode === 'liquidating').
  removalReasonCode: z.enum(['1', '2', '3', '4', '5', '6', '7']).optional(),
  liquidationInterestDue: z.number().optional(),
  liquidationPrincipalRemitted: z.number().optional(),
  liquidationPrincipalBalance: z.number().optional(),

  // ARM trio (active loans whose adjustment falls in the reporting period).
  armProspectiveInterestRate: z.number().optional(),
  armProspectiveMonthlyPnI: z.number().optional(),
  armAdjustmentEffectiveDate: zodDateOrString.optional(),

  sensitiveRecordEmitted: z.boolean().optional(),
  variousRecordEmitted: z.boolean().optional(),
});
export type GinnieMaeRfsReportedRecordV1 = z.infer<typeof GinnieMaeRfsReportedRecordV1>;

export const GinnieMaeRfsReportedRecord = GinnieMaeRfsReportedRecordV1;
export type GinnieMaeRfsReportedRecord = z.infer<typeof GinnieMaeRfsReportedRecord>;

// Name consumed by the `InvestorReportingData` discriminated union. Keep this
// alias so the union stays wired when the underlying shape evolves to a
// version-discriminated union later.
export const GinnieMaeRFS = GinnieMaeRfsReportedRecord;
export type GinnieMaeRFS = z.infer<typeof GinnieMaeRFS>;
